import {
  InputDay,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import { day } from '@vrplatform/utils';
import { Collapse } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
  useWatch,
} from 'react-hook-form';

export type PostingDateType = 'checkIn' | 'checkOut' | 'bookedAt' | 'custom';

type ReservationPostingDates = {
  bookedAt?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
};

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  reservationDates: ReservationPostingDates;
  label?: string;
  placeholder?: string;
  requiredMessage?: string;
  customDateRequiredMessage?: string;
  forcePostingDate?: boolean;
  minDate?: string;
  description?: string;
};

const getPostingDateOptionLabel = (
  label: string,
  dateValue: string | null | undefined
) => {
  if (!dateValue) return label;

  const parsedDate = day(dateValue);
  return parsedDate.isValid() ? `${label} (${parsedDate.format('ll')})` : label;
};

const getPostingDateValueFromType = (
  postingDateType: Exclude<PostingDateType, 'custom'>,
  reservationDates: ReservationPostingDates
) => {
  if (postingDateType === 'checkIn') return reservationDates.checkIn ?? null;
  if (postingDateType === 'checkOut') return reservationDates.checkOut ?? null;

  return reservationDates.bookedAt ?? null;
};

const isBeforeMinDate = (
  dateValue: string | null | undefined,
  minDate: string | undefined
) => {
  if (!dateValue || !minDate) return false;

  const parsedDate = day(dateValue);
  const parsedMinDate = day(minDate);

  if (!parsedDate.isValid() || !parsedMinDate.isValid()) return false;

  return parsedDate.isBefore(parsedMinDate, 'day');
};

const getPostingDateTypeFromValue = (
  postingAt: string | null,
  reservationDates: ReservationPostingDates
): PostingDateType | null => {
  if (!postingAt) return null;

  const parsedPostingAt = day(postingAt);
  if (!parsedPostingAt.isValid()) return null;

  if (
    reservationDates.checkIn &&
    parsedPostingAt.isSame(day(reservationDates.checkIn), 'day')
  ) {
    return 'checkIn';
  }

  if (
    reservationDates.checkOut &&
    parsedPostingAt.isSame(day(reservationDates.checkOut), 'day')
  ) {
    return 'checkOut';
  }

  if (
    reservationDates.bookedAt &&
    parsedPostingAt.isSame(day(reservationDates.bookedAt), 'day')
  ) {
    return 'bookedAt';
  }

  return 'custom';
};

const getDefaultCustomPostingDate = (minDate: string | undefined) => {
  const today = day();

  if (!minDate) return today.yyyymmdd();

  const parsedMinDate = day(minDate);
  if (!parsedMinDate.isValid()) return today.yyyymmdd();

  return today.isAfter(parsedMinDate, 'day')
    ? today.yyyymmdd()
    : parsedMinDate.yyyymmdd();
};

const parsePostingDateValue = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;

  return value.trim() ? value : null;
};

export const ReservationPostingDateSelectInput = <
  TFieldValues extends FieldValues,
>({
  control,
  name,
  reservationDates,
  label = 'Posting date',
  placeholder = 'Select posting date',
  requiredMessage = 'Please select a posting date',
  customDateRequiredMessage = 'Please select a custom posting date',
  forcePostingDate = false,
  minDate,
  description,
}: Props<TFieldValues>) => {
  const postingAt = parsePostingDateValue(useWatch({ control, name }));
  const [postingDateType, setPostingDateType] =
    useState<PostingDateType | null>(() =>
      getPostingDateTypeFromValue(postingAt, reservationDates)
    );

  const isForced = forcePostingDate || !!minDate;

  const postingDateOptions = useMemo<SelectItem<PostingDateType>[]>(
    () => [
      {
        label: getPostingDateOptionLabel('Check-in', reservationDates.checkIn),
        value: 'checkIn',
        disabled:
          !reservationDates.checkIn ||
          isBeforeMinDate(reservationDates.checkIn, minDate),
      },
      {
        label: getPostingDateOptionLabel(
          'Check-out',
          reservationDates.checkOut
        ),
        value: 'checkOut',
        disabled:
          !reservationDates.checkOut ||
          isBeforeMinDate(reservationDates.checkOut, minDate),
      },
      {
        label: getPostingDateOptionLabel(
          'Booked At',
          reservationDates.bookedAt
        ),
        value: 'bookedAt',
        disabled:
          !reservationDates.bookedAt ||
          isBeforeMinDate(reservationDates.bookedAt, minDate),
      },
      {
        label: 'Custom date',
        value: 'custom',
      },
    ],
    [
      minDate,
      reservationDates.bookedAt,
      reservationDates.checkIn,
      reservationDates.checkOut,
    ]
  );

  useEffect(() => {
    if (!postingAt) {
      setPostingDateType(null);
      return;
    }

    if (postingDateType === 'custom') return;

    setPostingDateType(
      getPostingDateTypeFromValue(postingAt, {
        bookedAt: reservationDates.bookedAt,
        checkIn: reservationDates.checkIn,
        checkOut: reservationDates.checkOut,
      })
    );
  }, [
    postingAt,
    postingDateType,
    reservationDates.bookedAt,
    reservationDates.checkIn,
    reservationDates.checkOut,
  ]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: (value) => {
          const resolvedPostingDate = parsePostingDateValue(value);

          if (isForced && !resolvedPostingDate) {
            return requiredMessage;
          }

          if (postingDateType === 'custom' && !resolvedPostingDate) {
            return customDateRequiredMessage;
          }

          if (
            resolvedPostingDate &&
            isBeforeMinDate(resolvedPostingDate, minDate)
          ) {
            return `Posting date cannot be before ${day(minDate).format('ll')}`;
          }

          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const hasCustomDateError =
          postingDateType === 'custom' && !!fieldState.error;

        return (
          <>
            <InputWrapper
              label={label}
              description={description}
              error={
                !hasCustomDateError ? fieldState.error?.message : undefined
              }
              required={isForced}
            >
              <InputSelect
                type="single"
                value={
                  postingDateOptions.find(
                    (option) => option.value === postingDateType
                  ) ?? null
                }
                setValue={(value) => {
                  const nextPostingDateType = value?.value ?? null;

                  setPostingDateType(nextPostingDateType);

                  if (!nextPostingDateType) {
                    field.onChange(null);
                    return;
                  }

                  if (nextPostingDateType === 'custom') {
                    const hasExistingCustomDate =
                      postingAt &&
                      getPostingDateTypeFromValue(
                        postingAt,
                        reservationDates
                      ) === 'custom' &&
                      !isBeforeMinDate(postingAt, minDate);

                    field.onChange(
                      hasExistingCustomDate
                        ? postingAt
                        : getDefaultCustomPostingDate(minDate)
                    );
                    return;
                  }

                  field.onChange(
                    getPostingDateValueFromType(
                      nextPostingDateType,
                      reservationDates
                    )
                  );
                }}
                data={{
                  options: postingDateOptions,
                }}
                inputProps={{
                  placeholder,
                  error: !hasCustomDateError && !!fieldState.error,
                  withClearButton: !isForced,
                }}
                dropdownProps={{
                  hideSearch: true,
                }}
              />
            </InputWrapper>

            <Collapse in={postingDateType === 'custom'}>
              <InputWrapper
                error={
                  hasCustomDateError ? fieldState.error?.message : undefined
                }
              >
                <InputDay
                  value={field.value ? day(String(field.value)).toDate() : null}
                  onChange={(value) => {
                    if (!value) return;

                    field.onChange(day(value).yyyymmdd());
                  }}
                  minDate={minDate ? day(minDate).toDate() : undefined}
                  placeholder="Select posting date"
                  error={hasCustomDateError}
                  clearable={false}
                />
              </InputWrapper>
            </Collapse>
          </>
        );
      }}
    />
  );
};
